import { z } from 'zod';
import type { EstadoVenta, MetodoPago } from '../estados';
import { crearClienteSchema } from './clientes';
import { centavosPositivos, motivo, uuid } from './comunes';

/** Toda venta queda vinculada a un cliente: uno existente o los datos de uno nuevo (se da de alta con la venta). */
export const crearVentaSchema = z
  .object({
    clienteId: uuid.optional(),
    clienteNuevo: crearClienteSchema.optional(),
    barberoId: uuid.nullable().optional(),
  })
  .refine((v) => !!v.clienteId !== !!v.clienteNuevo, {
    message: 'Indica un cliente existente o los datos de uno nuevo',
    path: ['clienteId'],
  });
export type CrearVentaInput = z.infer<typeof crearVentaSchema>;

export const agregarLineaSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('servicio'),
    servicioId: uuid,
    /** Barbero que realizó el servicio (recibe la comisión). */
    barberoId: uuid,
    cantidad: z.number().int().min(1).max(20).default(1),
  }),
  z.object({
    tipo: z.literal('producto'),
    productoId: uuid,
    cantidad: z.number().int().min(1).max(100).default(1),
  }),
]);
export type AgregarLineaInput = z.infer<typeof agregarLineaSchema>;

export const pagoSchema = z.object({
  metodo: z.enum(['efectivo', 'transferencia']),
  moneda: z.enum(['MXN', 'USD']).default('MXN'),
  monto: centavosPositivos.refine((v) => v > 0, 'El monto debe ser mayor a cero'),
  referencia: z.string().trim().max(100).optional(),
});

export const pagoCorreccionSchema = z.discriminatedUnion('metodo', [
  pagoSchema.extend({ metodo: z.literal('efectivo'), id: uuid.optional() }),
  pagoSchema.extend({ metodo: z.literal('transferencia'), moneda: z.literal('MXN').default('MXN'), id: uuid.optional() }),
]);
export const corregirCobroSchema = z.object({
  version: z.string().datetime(),
  motivo,
  ajuste: z.object({ lineaId: uuid, montoFinal: centavosPositivos }).optional(),
  pagos: z.array(pagoCorreccionSchema).max(4),
});
export type CorregirCobroInput = z.infer<typeof corregirCobroSchema>;

export const propinaSchema = z.object({
  monto: centavosPositivos.refine((v) => v > 0, 'La propina debe ser mayor a cero'),
  moneda: z.enum(['MXN', 'USD']).default('MXN'),
  metodo: z.enum(['efectivo', 'transferencia']).default('efectivo'),
  /** Sin barbero: se reparte entre los barberos que trabajaron ese día. */
  barberoId: uuid.nullable().optional(),
});
export type PropinaInput = z.infer<typeof propinaSchema>;

export const cobrarSchema = z.object({
  pagos: z.array(pagoSchema).max(6),
  propina: propinaSchema.optional(),
  clienteId: uuid.nullable().optional(),
});
export type CobrarInput = z.infer<typeof cobrarSchema>;

export const cancelarVentaSchema = z.object({ motivo });

export const devolucionSchema = z.object({
  lineas: z.array(z.object({ lineaId: uuid, cantidad: z.number().int().min(1) })).min(1),
  motivo,
  regresaAStock: z.boolean().default(false),
});
export type DevolucionInput = z.infer<typeof devolucionSchema>;

export interface VentaLineaVista {
  id: string;
  /** Servicio principal de la cita: no se puede quitar del carrito. */
  fija: boolean;
  tipo: 'servicio' | 'producto';
  /** Servicio de la línea (null en productos). */
  servicioId: string | null;
  /** Producto de la línea (null en servicios). */
  productoId: string | null;
  /** El cajero eligió esta línea para la promoción. */
  promocionElegida: boolean;
  descripcion: string;
  sku: string | null;
  cantidad: number;
  precioUnitario: number;
  descuento: number;
  ajusteCorreccion: number;
  total: number;
  neto: number;
  comisionista: { id: string; nombre: string } | null;
  cantidadDevuelta: number;
}

export type EstadoReembolsoTarjeta = 'pendiente' | 'aplicado' | 'error';

/** Reembolso automático a la tarjeta de una devolución, tal como lo ve la caja (sin datos de Mercado Pago). */
export interface ReembolsoTarjetaResumen {
  id: string;
  montoMXN: number;
  estado: EstadoReembolsoTarjeta;
  /** Código del fallo (p. ej. `MP_NO_DISPONIBLE`, `SIN_CONFIRMAR`) solo si `estado` es `error`. */
  codigoError: string | null;
  creadoEn: string;
}

/** Respuesta de `POST /reembolsos-tarjeta/:id/reintentar`. */
export interface ReembolsoTarjetaVista {
  id: string;
  devolucionId: string;
  ventaId: string;
  montoMXN: number;
  estado: EstadoReembolsoTarjeta;
  detalleError: string | null;
}

export interface VentaVista {
  id: string;
  folio: number;
  estado: EstadoVenta;
  origen: 'app' | 'pos' | 'importacion';
  cliente: { id: string; nombre: string; telefono: string } | null;
  barbero: { id: string; nombre: string } | null;
  citaId: string | null;
  subtotal: number;
  descuento: number;
  ajusteCorreccion: number;
  total: number;
  promocion: { id: string; nombre: string } | null;
  /** true = la promoción solo descuenta las líneas elegidas. */
  promocionSoloElegidas: boolean;
  lineas: VentaLineaVista[];
  pagos: { id: string; metodo: string; moneda: 'MXN' | 'USD'; monto: number; tipoCambio: number; montoMXN: number; referencia: string | null }[];
  cambioMXN: number;
  cobradaPor: string | null;
  pagadaEn: string | null;
  suspendidaEn: string | null;
  motivoCancelacion: string | null;
  creadaEn: string;
  version: string;
  /** Reembolsos a tarjeta de sus devoluciones, del más antiguo al más reciente. */
  reembolsosTarjeta: ReembolsoTarjetaResumen[];
}

/** Detalle de solo lectura de una venta (agenda y Clientes > Compras). */
export interface VentaDetalle extends VentaVista {
  /** `barbero` null: la propina se reparte entre todos los barberos del día. */
  propinas: { monto: number; moneda: 'MXN' | 'USD'; montoMXN: number; metodo: MetodoPago; barbero: string | null }[];
  /** Devoluciones por autorizar o aplicadas (las rechazadas no se muestran). */
  devoluciones: { monto: number; motivo: string; estado: 'pendiente' | 'aplicada'; fecha: string }[];
  canceladaEn: string | null;
}
